import React, { useEffect } from 'react';
import { Trophy, BarChart2 } from 'lucide-react';
import { GameStats, GameMode, Operation } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';

interface GameSummaryProps {
  stats: GameStats;
  mode: GameMode;
  operation: Operation;
  onPlayAgain: () => void;
}

const GameSummary: React.FC<GameSummaryProps> = ({ stats, mode, operation, onPlayAgain }) => {
  const accuracy = stats.questionsAnswered > 0 
    ? Math.round((stats.correctAnswers / stats.questionsAnswered) * 100) 
    : 0;
  
  const getPerformanceMessage = () => {
    if (accuracy >= 90) return "Excellent! You're a math master!";
    if (accuracy >= 75) return "Great job! Keep practicing to improve further!";
    if (accuracy >= 50) return "Good effort! More practice will help you improve.";
    return "Keep practicing! You'll get better with time.";
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
      <div className="flex justify-center mb-6">
        <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-full">
          <Trophy className="h-12 w-12 text-indigo-600 dark:text-indigo-400" />
        </div>
      </div>
      
      <h2 className="text-2xl font-bold text-center text-slate-800 dark:text-slate-100 mb-6">
        {mode === 'timed' ? 'Time\'s Up!' : 'Practice Complete!'}
      </h2>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
          <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">{stats.questionsAnswered}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Questions</div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
          <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">{stats.correctAnswers}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Correct</div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
          <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">{accuracy}%</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Accuracy</div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
          <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">
            {stats.averageResponseTime.toFixed(1)}s
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Avg. Time</div>
        </div>
      </div>
      
      <p className="text-center text-slate-700 dark:text-slate-300 mb-6">
        {getPerformanceMessage()}
      </p>
      
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onPlayAgain}
          className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
            hover:bg-indigo-700 dark:hover:bg-indigo-600 
            focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2
            transition-colors"
        >
          Play Again
        </button>    
      </div>
    </div>
  );
};

export default GameSummary;